import React from "react";
import { Avatar, Divider, IconButton, Stack, Typography, TypographyProps } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";

import { IDealCryptocurrency } from "store/types";
import { CurrencyPerformanceTable } from "./CurrencyPerformanceTable";
import LineChart from "./LineChart";
import { DealRoleGuard } from "components/guards";
import { RoleName } from "../../../constants";

interface Props {
  changeCrypto: () => void;
  data: IDealCryptocurrency;
}

const oneRowTextStyle: TypographyProps["sx"] = {
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  overflow: "hidden"
};

export const Content: React.FC<Props> = ({
  changeCrypto,
  data: { name, image, price, previous_close, market_cap, circulating_supply, historical_data, graph_data, link }
}) => {
  return (
    <Stack sx={{ gap: "24px", alignItems: "stretch", overflow: "hidden" }}>
      <Stack direction="row" gap="8px" sx={{ overflow: "hidden" }}>
        <Avatar src={image} sx={{ width: "52px", height: "52px", borderRadius: 0 }} variant="square" />
        <Stack sx={{ overflow: "hidden" }}>
          <Typography sx={{ fontSize: "20px", fontWeight: 700, color: "#0A3142", ...oneRowTextStyle }}>
            {name}
          </Typography>
          <Stack flexDirection="row" gap="10px">
            <Typography sx={{ fontSize: "14px", color: "rgba(73, 102, 130, 1)" }}>{link}</Typography>
            <DealRoleGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
              <IconButton onClick={changeCrypto} sx={{ alignSelf: "flex-start", width: "25px", height: "25px" }}>
                <EditOutlinedIcon sx={{ fontSize: "18px", color: "rgba(73, 102, 130, 1)" }} />
              </IconButton>
            </DealRoleGuard>
          </Stack>
        </Stack>
      </Stack>
      <Stack flexDirection="row" width="100%" justifyContent="space-between" gap="20px">
        <Stack flexDirection="column" width="50%">
          <Stack>
            <Typography variant="caption" color="rgba(73, 102, 130, 1)" fontSize="12px">
              PRICE
            </Typography>
            <Typography variant="h5" fontWeight="bold" color="rgba(7, 31, 53, 1)" fontSize="24px">
              {price}
            </Typography>
          </Stack>
          <Stack>
            <Typography variant="caption" color="rgba(73, 102, 130, 1)" fontSize="12px">
              PREVIOUS CLOSE
            </Typography>
            <Typography variant="h5" fontWeight="bold" color="rgba(73, 102, 130, 1)" fontSize="24px">
              {previous_close}
            </Typography>
          </Stack>
        </Stack>
        <Divider orientation="vertical" flexItem sx={{ borderColor: "rgba(219, 232, 245, 1)" }} />
        <Stack flexDirection="column" width="50%">
          <Stack>
            <Typography variant="caption" color="rgba(73, 102, 130, 1)" fontSize="12px">
              MARKET CAP
            </Typography>
            <Typography variant="h5" fontWeight="bold" color="rgba(73, 102, 130, 1)" fontSize="24px">
              {market_cap}
            </Typography>
          </Stack>
          <Stack>
            <Typography variant="caption" color="rgba(73, 102, 130, 1)" fontSize="12px">
              CIRCULATING SUPPLY
            </Typography>
            <Typography variant="h5" fontWeight="bold" color="rgba(73, 102, 130, 1)" fontSize="24px">
              {circulating_supply}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
      {Boolean(graph_data.length) && (
        <Stack>
          <LineChart data={graph_data} />
        </Stack>
      )}
      <Stack gap="20px">
        <Typography variant="h6" sx={{ fontWeight: 500, color: "rgba(7, 31, 53, 1)" }}>
          Currency performance
        </Typography>
        <CurrencyPerformanceTable rows={historical_data} />
      </Stack>
    </Stack>
  );
};
